import React from 'react';
import { FilterDropdown } from './FilterDropdown';
import { XIcon } from 'lucide-react';
interface FilterPanelProps {
  selectedFilters: {
    division: string;
    district: string;
    upazila: string;
    mouja: string;
    sheetNo: string;
  };
  onFilterChange: (filters: any) => void;
  onClose?: () => void;
}
export const FilterPanel = ({
  selectedFilters,
  onFilterChange,
  onClose
}: FilterPanelProps) => {
  // Real data for Bangladesh administrative divisions
  const divisions = [{
    id: 'dhaka',
    name: 'ঢাকা',
    nameEn: 'Dhaka'
  }, {
    id: 'chittagong',
    name: 'চট্টগ্রাম',
    nameEn: 'Chittagong'
  }, {
    id: 'rajshahi',
    name: 'রাজশাহী',
    nameEn: 'Rajshahi'
  }, {
    id: 'khulna',
    name: 'খুলনা',
    nameEn: 'Khulna'
  }, {
    id: 'barisal',
    name: 'বরিশাল',
    nameEn: 'Barisal'
  }, {
    id: 'sylhet',
    name: 'সিলেট',
    nameEn: 'Sylhet'
  }, {
    id: 'rangpur',
    name: 'রংপুর',
    nameEn: 'Rangpur'
  }, {
    id: 'mymensingh',
    name: 'ময়মনসিংহ',
    nameEn: 'Mymensingh'
  }];
  // Real districts for Dhaka division
  const districtsByDivision = {
    dhaka: [{
      id: 'dhaka_district',
      name: 'ঢাকা',
      nameEn: 'Dhaka',
      coordinates: [23.7104, 90.4074],
      zoomLevel: 10
    }, {
      id: 'gazipur',
      name: 'গাজীপুর',
      nameEn: 'Gazipur',
      coordinates: [24.0958, 90.4125],
      zoomLevel: 10
    }, {
      id: 'narayanganj',
      name: 'নারায়ণগঞ্জ',
      nameEn: 'Narayanganj',
      coordinates: [23.6238, 90.5],
      zoomLevel: 11
    }, {
      id: 'tangail',
      name: 'টাঙ্গাইল',
      nameEn: 'Tangail',
      coordinates: [24.2498, 89.9165],
      zoomLevel: 10
    }, {
      id: 'narsingdi',
      name: 'নরসিংদী',
      nameEn: 'Narsingdi',
      coordinates: [23.9322, 90.7157],
      zoomLevel: 11
    }, {
      id: 'munshiganj',
      name: 'মুন্সিগঞ্জ',
      nameEn: 'Munshiganj',
      coordinates: [23.5422, 90.5305],
      zoomLevel: 11
    }],
    // Other divisions would have their own districts
    chittagong: [],
    rajshahi: [],
    khulna: [],
    barisal: [],
    sylhet: [],
    rangpur: [],
    mymensingh: []
  };
  // Real upazilas for Dhaka districts
  const upazilasByDistrict = {
    dhaka_district: [{
      id: 'dhaka_north',
      name: 'ঢাকা উত্তর সিটি',
      nameEn: 'Dhaka North City',
      coordinates: [23.8103, 90.4125],
      zoomLevel: 12
    }, {
      id: 'dhaka_south',
      name: 'ঢাকা দক্ষিণ সিটি',
      nameEn: 'Dhaka South City',
      coordinates: [23.7104, 90.4074],
      zoomLevel: 12
    }, {
      id: 'savar',
      name: 'সাভার',
      nameEn: 'Savar',
      coordinates: [23.8583, 90.25],
      zoomLevel: 12
    }, {
      id: 'keraniganj',
      name: 'কেরাণীগঞ্জ',
      nameEn: 'Keraniganj',
      coordinates: [23.7834, 90.3874],
      zoomLevel: 12
    }, {
      id: 'dhamrai',
      name: 'ধামরাই',
      nameEn: 'Dhamrai',
      coordinates: [23.9167, 90.2167],
      zoomLevel: 12
    }],
    gazipur: [{
      id: 'gazipur_sadar',
      name: 'গাজীপুর সদর',
      nameEn: 'Gazipur Sadar',
      coordinates: [24.0958, 90.4125],
      zoomLevel: 12
    }, {
      id: 'kaliakair',
      name: 'কালিয়াকৈর',
      nameEn: 'Kaliakair',
      coordinates: [24.1083, 90.275],
      zoomLevel: 12
    }, {
      id: 'kapasia',
      name: 'কাপাসিয়া',
      nameEn: 'Kapasia',
      coordinates: [24.1833, 90.5667],
      zoomLevel: 12
    }],
    narayanganj: [{
      id: 'narayanganj_sadar',
      name: 'নারায়ণগঞ্জ সদর',
      nameEn: 'Narayanganj Sadar',
      coordinates: [23.6238, 90.5],
      zoomLevel: 12
    }, {
      id: 'rupganj',
      name: 'রূপগঞ্জ',
      nameEn: 'Rupganj',
      coordinates: [23.75, 90.57],
      zoomLevel: 12
    }, {
      id: 'sonargaon',
      name: 'সোনারগাঁও',
      nameEn: 'Sonargaon',
      coordinates: [23.65, 90.6],
      zoomLevel: 12
    }]
    // Add more upazilas for other districts
  };
  // Real moujas for Dhaka upazilas
  const moujasByUpazila = {
    dhaka_north: [{
      id: 'uttara',
      name: 'উত্তরা',
      nameEn: 'Uttara',
      coordinates: [23.8758, 90.3795],
      zoomLevel: 14
    }, {
      id: 'mirpur',
      name: 'মিরপুর',
      nameEn: 'Mirpur',
      coordinates: [23.8223, 90.3654],
      zoomLevel: 14
    }, {
      id: 'gulshan',
      name: 'গুলশান',
      nameEn: 'Gulshan',
      coordinates: [23.7931, 90.4137],
      zoomLevel: 14
    }],
    dhaka_south: [{
      id: 'motijheel',
      name: 'মতিঝিল',
      nameEn: 'Motijheel',
      coordinates: [23.7331, 90.4173],
      zoomLevel: 14
    }, {
      id: 'lalbagh',
      name: 'লালবাগ',
      nameEn: 'Lalbagh',
      coordinates: [23.7179, 90.3872],
      zoomLevel: 14
    }, {
      id: 'dhanmondi',
      name: 'ধানমন্ডি',
      nameEn: 'Dhanmondi',
      coordinates: [23.7461, 90.3742],
      zoomLevel: 14
    }],
    savar: [{
      id: 'savar_sadar',
      name: 'সাভার সদর',
      nameEn: 'Savar Sadar',
      coordinates: [23.8583, 90.25],
      zoomLevel: 14
    }, {
      id: 'ashulia',
      name: 'আশুলিয়া',
      nameEn: 'Ashulia',
      coordinates: [23.8917, 90.3167],
      zoomLevel: 14
    }]
    // Add more moujas for other upazilas
  };
  // Sheet numbers by mouja
  const sheetNosByMouja = {
    uttara: [{
      id: 'uttara_sheet1',
      name: 'শিট ১',
      nameEn: 'Sheet 1',
      coordinates: [23.8758, 90.3795],
      zoomLevel: 16
    }, {
      id: 'uttara_sheet2',
      name: 'শিট ২',
      nameEn: 'Sheet 2',
      coordinates: [23.8748, 90.3815],
      zoomLevel: 16
    }],
    mirpur: [{
      id: 'mirpur_sheet1',
      name: 'শিট ১',
      nameEn: 'Sheet 1',
      coordinates: [23.8223, 90.3654],
      zoomLevel: 16
    }, {
      id: 'mirpur_sheet2',
      name: 'শিট ২',
      nameEn: 'Sheet 2',
      coordinates: [23.8243, 90.3674],
      zoomLevel: 16
    }]
    // Add more sheets for other moujas
  };
  // Get the appropriate list based on selected filters
  const districts = selectedFilters.division ? districtsByDivision[selectedFilters.division] || [] : [];
  const upazilas = selectedFilters.district ? upazilasByDistrict[selectedFilters.district] || [] : [];
  const moujas = selectedFilters.upazila ? moujasByUpazila[selectedFilters.upazila] || [] : [];
  const sheetNos = selectedFilters.mouja ? sheetNosByMouja[selectedFilters.mouja] || [] : [];
  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...selectedFilters
    };
    // Reset dependent filters
    if (key === 'division') {
      newFilters.district = '';
      newFilters.upazila = '';
      newFilters.mouja = '';
      newFilters.sheetNo = '';
    } else if (key === 'district') {
      newFilters.upazila = '';
      newFilters.mouja = '';
      newFilters.sheetNo = '';
    } else if (key === 'upazila') {
      newFilters.mouja = '';
      newFilters.sheetNo = '';
    } else if (key === 'mouja') {
      newFilters.sheetNo = '';
    }
    newFilters[key] = value;
    onFilterChange(newFilters);
  };
  return <div className="h-full flex flex-col bg-white" data-id="element-158">
      <div className="p-4 border-b flex justify-between items-center" data-id="element-159">
        <h2 className="text-lg font-semibold" data-id="element-160">ফিল্টার</h2>
        {onClose && <button className="p-1 rounded-full hover:bg-gray-200" onClick={onClose} data-id="element-161">
            <XIcon className="h-5 w-5" data-id="element-162" />
          </button>}
      </div>
      <div className="p-4 flex-1 overflow-y-auto" data-id="element-163">
        <div className="space-y-4" data-id="element-164">
          <FilterDropdown label="বিভাগ (Division)" options={divisions} value={selectedFilters.division} onChange={value => handleFilterChange('division', value)} data-id="element-165" />
          <FilterDropdown label="জেলা (District)" options={districts} value={selectedFilters.district} onChange={value => handleFilterChange('district', value)} disabled={!selectedFilters.division} data-id="element-166" />
          <FilterDropdown label="উপজেলা (Upazila)" options={upazilas} value={selectedFilters.upazila} onChange={value => handleFilterChange('upazila', value)} disabled={!selectedFilters.district} data-id="element-167" />
          <FilterDropdown label="মৌজা (Mouja)" options={moujas} value={selectedFilters.mouja} onChange={value => handleFilterChange('mouja', value)} disabled={!selectedFilters.upazila} data-id="element-168" />
          <FilterDropdown label="শিট নং (Sheet No)" options={sheetNos} value={selectedFilters.sheetNo} onChange={value => handleFilterChange('sheetNo', value)} disabled={!selectedFilters.mouja} data-id="element-169" />
        </div>
        {!selectedFilters.division && <div className="mt-6 p-4 bg-blue-50 text-blue-800 rounded-md text-sm" data-id="element-170">
            অনুগ্রহ করে বিভাগ থেকে শুরু করে ফিল্টার নির্বাচন করুন।
            <p className="text-xs mt-1 text-blue-600" data-id="element-171">
              (Please select filters starting from Division.)
            </p>
          </div>}
      </div>
    </div>;
};